/**
 * German output formatting.
 *
 * These are the only strings in the code base that are intentionally German:
 * everything here ends up on screen. The exact shapes are taken from the design
 * reference (en dash with spaces in ranges, comma as decimal separator, "T"/"h"
 * duration units, `24:00` as a first-class end time).
 */

import {
  MINUTES_PER_DAY,
  MINUTES_PER_SLOT,
  SLOTS_PER_DAY,
  dayOfSlot,
  localDateFromDay,
  slotOfDay,
  weekdayOfDay,
} from './slots.js';

/** Monday-first, matching {@link weekdayOfDay}. */
export const WEEKDAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'] as const;

export const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
] as const;

export const MONTHS_LONG = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
] as const;

/** U+2013, always surrounded by spaces in ranges. */
export const EN_DASH = '–';
/** U+00B7, separates meta segments such as "seit … · danach: …". */
export const MIDDOT = '·';
/** U+2192, used by the range selection pill and the changelog before/after pair. */
export const ARROW = '→';

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function germanNumber(value: number): string {
  return String(value).replace('.', ',');
}

/**
 * `0 … 48` → `"00:00" … "24:00"`. Slot 48 is the end of the day and is shown as
 * `24:00`, never as `00:00` of the following day.
 */
export function formatSlotTime(slotInDay: number): string {
  return `${pad2(Math.floor(slotInDay / 2))}:${pad2((slotInDay % 2) * MINUTES_PER_SLOT)}`;
}

/**
 * Duration of a slot count: `8 h`, `1 T`, `5 T 16 h`, `5,5 h`. Hours are shown
 * in half-hour precision; whole days are never folded into hours and vice
 * versa (`1440` minutes reads as `1 T`, not `24 h`).
 */
export function formatDuration(slots: number): string {
  const totalMinutes = slots * MINUTES_PER_SLOT;
  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const hours = (totalMinutes - days * MINUTES_PER_DAY) / 60;
  const hoursLabel = germanNumber(hours);
  if (days > 0 && hours > 0) return `${days} T ${hoursLabel} h`;
  if (days > 0) return `${days} T`;
  return `${hoursLabel} h`;
}

/**
 * Share of total time with exactly one decimal, German comma and a space
 * before the percent sign — e.g. `44,6 %`, `4,0 %`.
 */
export function formatPercent(fraction: number): string {
  return `${(fraction * 100).toFixed(1).replace('.', ',')} %`;
}

/** Bar width for inline styles. CSS needs a dot as decimal separator. */
export function formatCssPercent(fraction: number): string {
  return `${Math.round(fraction * 10000) / 100}%`;
}

export function weekdayLabel(dayIndex: number): string {
  return WEEKDAYS_SHORT[weekdayOfDay(dayIndex)] as string;
}

/** `Fr, 21.08.` */
export function formatDayShort(dayIndex: number): string {
  const { month, day } = localDateFromDay(dayIndex);
  return `${weekdayLabel(dayIndex)}, ${pad2(day)}.${pad2(month)}.`;
}

/** `Fr, 21.08.2026` */
export function formatDayLong(dayIndex: number): string {
  const { year, month, day } = localDateFromDay(dayIndex);
  return `${weekdayLabel(dayIndex)}, ${pad2(day)}.${pad2(month)}.${year}`;
}

/** `Do, 20. August 2026` — used for sheet titles. */
export function formatDayFull(dayIndex: number): string {
  const { year, month, day } = localDateFromDay(dayIndex);
  return `${weekdayLabel(dayIndex)}, ${day}. ${MONTHS_LONG[month - 1]} ${year}`;
}

/** `21.08.2026` without weekday. */
export function formatDateNumeric(dayIndex: number): string {
  const { year, month, day } = localDateFromDay(dayIndex);
  return `${pad2(day)}.${pad2(month)}.${year}`;
}

/** `August 2026` */
export function formatMonthYear(year: number, month: number): string {
  return `${MONTHS_LONG[month - 1]} ${year}`;
}

/** `Sa, 1. August 2026, 09:00` — the date line of the "Aktuell" header. */
export function formatDateTimeHeader(now: Date): string {
  const dayIndex = Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86_400_000);
  const { year, month, day } = localDateFromDay(dayIndex);
  return `${weekdayLabel(dayIndex)}, ${day}. ${MONTHS_LONG[month - 1]} ${year}, ${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
}

/**
 * An assignment end that falls exactly on a day boundary belongs to the
 * previous day and is displayed as `24:00`. Returns the day the end time
 * should be rendered on plus its slot-in-day (`0 … 48`).
 */
export function displayEndOfSlot(endSlot: number): { dayIndex: number; slotInDay: number } {
  const inDay = slotOfDay(endSlot);
  if (inDay === 0) return { dayIndex: dayOfSlot(endSlot) - 1, slotInDay: SLOTS_PER_DAY };
  return { dayIndex: dayOfSlot(endSlot), slotInDay: inDay };
}

export interface RangeFormatOptions {
  /** Include the year on both dates — used in the modal and the changelog. */
  withYear?: boolean;
}

/**
 * `Sa, 01.08., 10:00 – 18:00` when start and end share a day, otherwise
 * `Sa, 01.08., 18:00 – So, 02.08., 18:00`.
 */
export function formatSlotRange(
  startSlot: number,
  endSlot: number,
  options: RangeFormatOptions = {},
): string {
  const formatDate = options.withYear ? formatDayLong : formatDayShort;
  const startDay = dayOfSlot(startSlot);
  const startTime = formatSlotTime(slotOfDay(startSlot));
  const end = displayEndOfSlot(endSlot);
  const endTime = formatSlotTime(end.slotInDay);

  if (end.dayIndex === startDay) {
    return `${formatDate(startDay)}, ${startTime} ${EN_DASH} ${endTime}`;
  }
  return `${formatDate(startDay)}, ${startTime} ${EN_DASH} ${formatDate(end.dayIndex)}, ${endTime}`;
}

/** `10:00 – 18:00` — range without any date, for the day timeline. */
export function formatTimeRange(startSlot: number, endSlot: number): string {
  return `${formatSlotTime(slotOfDay(startSlot))} ${EN_DASH} ${formatSlotTime(displayEndOfSlot(endSlot).slotInDay)}`;
}

/**
 * Changelog timestamps: `Heute, 09:12`, `Gestern, 21:40`, otherwise
 * `28.07.2026, 19:03`.
 */
export function formatLogTimestamp(ts: number, now: Date = new Date()): string {
  const date = new Date(ts);
  const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  const dayOf = (value: Date) =>
    Math.floor(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / 86_400_000);
  const diff = dayOf(now) - dayOf(date);
  if (diff === 0) return `Heute, ${time}`;
  if (diff === 1) return `Gestern, ${time}`;
  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}, ${time}`;
}

/** `31 Tage · 744 h` — footer of the monthly statistics. */
export function formatTotalDays(days: number): string {
  return `${days} ${days === 1 ? 'Tag' : 'Tage'} ${MIDDOT} ${days * 24} h`;
}
